import React from 'react';
import { DEFAULT_ACTIVITY_TIMEZONE } from '../utils/activityDateTime';
import { fetchMe, getCachedMe, invalidateMeCache, subscribeMeCacheInvalidation } from '../lib/meClient';
import type { MeResponse } from '../lib/meClient';

type CompanyTimezoneContextValue = {
  timeZone: string;
  ready: boolean;
  refresh: () => Promise<void>;
};

const CompanyTimezoneContext = React.createContext<CompanyTimezoneContextValue>({
  timeZone: DEFAULT_ACTIVITY_TIMEZONE,
  ready: false,
  refresh: async () => {},
});

function timezoneFromMePayload(data: { timezone?: unknown } | null | undefined): string {
  const tz = typeof data?.timezone === 'string' ? data.timezone.trim() : '';
  return tz || DEFAULT_ACTIVITY_TIMEZONE;
}

type EnsureResult =
  | { ok: true; me: MeResponse | null }
  /** Session could not be aligned with the URL instance — do not trust previous timezone. */
  | { ok: false };

/**
 * Align session active instance with the URL instance before reading timezone,
 * so deep-links do not briefly (or permanently until next nav) use the previous company zone.
 */
async function ensureSessionMatchesUrlInstance(
  instanceKey: string | null | undefined,
): Promise<EnsureResult> {
  const key = String(instanceKey ?? '').trim();
  if (!key || key === 'default') {
    return { ok: true, me: await fetchMe({ force: true }) };
  }

  const me = await fetchMe({ force: true });
  if (!me) return { ok: true, me: null };

  const activeId = String(me.activeInstance?.id ?? '').trim();
  if (activeId === key) return { ok: true, me };

  const instances = Array.isArray(me.instances) ? me.instances : [];
  if (!instances.some((i) => String(i.id) === key)) {
    // URL instance is not accessible — fall back to current session timezone.
    return { ok: true, me };
  }

  try {
    const res = await fetch('/api/me/active-instance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ instanceId: key }),
    });
    if (!res.ok) return { ok: false };
    invalidateMeCache();
    try {
      localStorage.setItem('activeInstanceId', key);
    } catch {
      // noop
    }
    return { ok: true, me: await fetchMe({ force: true }) };
  } catch {
    return { ok: false };
  }
}

/**
 * Provides company IANA timezone from /api/me (country_code → zone, else Europe/Berlin).
 * Ensures the session active instance matches the URL before applying timezone.
 */
export function CompanyTimezoneProvider({
  children,
  instanceKey,
}: {
  children: React.ReactNode;
  /** Typically active instance id from the URL (`/i/:instanceId/...`). */
  instanceKey?: string | null;
}) {
  const [timeZone, setTimeZone] = React.useState(() =>
    timezoneFromMePayload(getCachedMe() as { timezone?: unknown } | null),
  );
  const [ready, setReady] = React.useState(false);

  const applyResult = React.useCallback((result: EnsureResult) => {
    if (!result.ok) {
      // Prefer safe default over a wrong company's zone after a failed switch.
      setTimeZone(DEFAULT_ACTIVITY_TIMEZONE);
      setReady(true);
      return;
    }
    setTimeZone(timezoneFromMePayload(result.me as { timezone?: unknown } | null));
    setReady(true);
  }, []);

  const refresh = React.useCallback(async () => {
    const result = await ensureSessionMatchesUrlInstance(instanceKey);
    applyResult(result);
  }, [instanceKey, applyResult]);

  React.useEffect(() => {
    let cancelled = false;
    const apply = (result: EnsureResult) => {
      if (cancelled) return;
      applyResult(result);
    };

    void ensureSessionMatchesUrlInstance(instanceKey)
      .then(apply)
      .catch(() => {
        if (cancelled) return;
        setTimeZone(DEFAULT_ACTIVITY_TIMEZONE);
        setReady(true);
      });

    const unsubscribe = subscribeMeCacheInvalidation(() => {
      void ensureSessionMatchesUrlInstance(instanceKey)
        .then(apply)
        .catch(() => {});
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [instanceKey, applyResult]);

  const value = React.useMemo(
    () => ({ timeZone, ready, refresh }),
    [timeZone, ready, refresh],
  );

  return (
    <CompanyTimezoneContext.Provider value={value}>{children}</CompanyTimezoneContext.Provider>
  );
}

export function useCompanyTimezone(): string {
  return React.useContext(CompanyTimezoneContext).timeZone;
}

export function useCompanyTimezoneState(): CompanyTimezoneContextValue {
  return React.useContext(CompanyTimezoneContext);
}
